import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Lock, Zap, Sparkles, AlertCircle, Heart, Users, ChevronDown, X, Shield, Swords } from 'lucide-react';
import { studentAPI, powerAPI } from '../utils/api';
import { getMonsterGif, getMonsterGifByMonster, getMonsterFallbackGif, getClassBadge, getClassName, loadUploadedGifs, subscribeToCacheUpdates, getMonsterDisplayName } from '../utils/helpers';
import { toast } from 'sonner';
import PokemonImage from '../components/PokemonImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const POWER_VERSION_CHECK_INTERVAL = 5000;

const StudentPowers = () => {
  const navigate = useNavigate();
  const [monsterData, setMonsterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usingPower, setUsingPower] = useState(null);
  const [gifCacheReady, setGifCacheReady] = useState(false);
  const [powerCostsVersion, setPowerCostsVersion] = useState(0);
  const [showCostChangeAlert, setShowCostChangeAlert] = useState(false);
  const versionCheckRef = useRef(null);

  // Cooperative power state
  const [showCoopModal, setShowCoopModal] = useState(false);
  const [coopPower, setCoopPower] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupName, setGroupName] = useState(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [coopSuccess, setCoopSuccess] = useState(null); // Animation state

  const loadMonsterData = useCallback(async () => {
    try {
      const response = await studentAPI.getMonster();
      setMonsterData(response.data);
    } catch (error) {
      console.error('Failed to load monster data:', error);
      toast.error('Erro ao carregar dados do monstro');
      navigate('/student/dashboard');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const checkPowerCostsVersion = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/powers/version`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.version > powerCostsVersion && powerCostsVersion > 0) {
          setShowCostChangeAlert(true);
          toast.info('O custo das habilidades foi ajustado pelo Mestre!', { duration: 5000 });
          await loadMonsterData();
          setTimeout(() => setShowCostChangeAlert(false), 5000);
        }
        setPowerCostsVersion(data.version);
      }
    } catch (error) {
      console.error('Failed to check power costs version:', error);
    }
  }, [powerCostsVersion, loadMonsterData]);

  useEffect(() => {
    loadMonsterData();
    loadUploadedGifs().then(() => setGifCacheReady(true));
    const unsubscribe = subscribeToCacheUpdates(() => setGifCacheReady(true));
    checkPowerCostsVersion();
    versionCheckRef.current = setInterval(checkPowerCostsVersion, POWER_VERSION_CHECK_INTERVAL);
    return () => {
      unsubscribe();
      if (versionCheckRef.current) clearInterval(versionCheckRef.current);
    };
  }, [loadMonsterData, checkPowerCostsVersion]);

  // Load group members for cooperative power
  const loadGroupMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const response = await powerAPI.getGroupMembers();
      setGroupMembers(response.data.members || []);
      setGroupName(response.data.group_name);
    } catch (error) {
      console.error('Failed to load group members:', error);
      setGroupMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  }, []);

  const handleCoopPowerClick = async (power) => {
    if (!monsterData?.has_group) {
      toast.error('Você precisa estar em um grupo para usar poderes cooperativos!');
      return;
    }
    setCoopPower(power);
    setSelectedTargets([]);
    setShowCoopModal(true);
    await loadGroupMembers();
  };

  const handleSelectTarget = (studentId) => {
    setSelectedTargets(prev => {
      if (prev.includes(studentId)) return prev.filter(id => id !== studentId);
      return [...prev, studentId];
    });
  };

  const handleSelectWholeGroup = () => {
    if (selectedTargets.length === groupMembers.length) {
      setSelectedTargets([]);
    } else {
      setSelectedTargets(groupMembers.map(m => m.student_id));
    }
  };

  const getCoopTotalCost = () => {
    if (!coopPower) return 0;
    const numTargets = selectedTargets.length;
    return coopPower.energy_cost * numTargets;
  };

  const canAffordCoop = () => {
    if (!monsterData) return false;
    return monsterData.student.energy >= getCoopTotalCost();
  };

  const handleUseCoopPower = async () => {
    if (!coopPower || selectedTargets.length === 0) return;
    const isWholeGroup = selectedTargets.length === groupMembers.length;

    setUsingPower(coopPower.power_id);
    try {
      // Optimistic energy deduction
      const totalCost = getCoopTotalCost();
      const newEnergy = monsterData.student.energy - totalCost;
      setMonsterData(prev => ({
        ...prev,
        student: { ...prev.student, energy: newEnergy }
      }));

      const response = await powerAPI.useCooperativePower(
        coopPower.power_id,
        isWholeGroup ? [] : selectedTargets,
        isWholeGroup
      );

      // Update with server response
      setMonsterData(prev => ({
        ...prev,
        student: { ...prev.student, energy: response.data.new_energy }
      }));

      // Show success animation
      setCoopSuccess({
        targets: response.data.targets_affected,
        effectType: response.data.effect_type,
        effectValue: response.data.effect_value,
        powerName: coopPower.name
      });

      // Close modal after animation
      setTimeout(() => {
        setCoopSuccess(null);
        setShowCoopModal(false);
        setCoopPower(null);
        setSelectedTargets([]);
      }, 2500);

      const effectEmoji = response.data.effect_type === 'hp' ? 'HP' : 'Energia';
      toast.success(`${coopPower.name} ativado! +${response.data.effect_value} ${effectEmoji} para ${response.data.targets_affected.length} aliado(s)!`);
    } catch (error) {
      console.error('Failed to use cooperative power:', error);
      loadMonsterData();
      toast.error(error.response?.data?.detail || 'Erro ao usar poder cooperativo');
    } finally {
      setUsingPower(null);
    }
  };

  const handleUsePower = async (power) => {
    if (!monsterData) return;
    const currentStudentEnergy = monsterData.student.energy;
    setUsingPower(power.power_id);
    try {
      const costCheckResponse = await fetch(`${BACKEND_URL}/api/powers/${power.power_id}/current-cost`, { credentials: 'include' });
      if (!costCheckResponse.ok) throw new Error('Failed to verify power cost');
      const costData = await costCheckResponse.json();
      const actualCost = costData.energy_cost;
      if (actualCost !== power.energy_cost) {
        toast.warning(`O custo de ${power.name} foi alterado para ${actualCost} de energia!`, { duration: 4000 });
        await loadMonsterData();
        setUsingPower(null);
        return;
      }
      if (currentStudentEnergy < actualCost) {
        toast.error(`Energia Insuficiente! Custo: ${actualCost}`);
        setUsingPower(null);
        return;
      }
      const newEnergy = currentStudentEnergy - actualCost;
      setMonsterData(prev => ({ ...prev, student: { ...prev.student, energy: newEnergy } }));
      const response = await powerAPI.usePower(power.power_id);
      setMonsterData(prev => ({ ...prev, student: { ...prev.student, energy: response.data.new_energy } }));
      toast.success(`${power.name} ativado!`, { description: `Energia restante: ${response.data.new_energy}` });
    } catch (error) {
      console.error('Failed to use power:', error);
      loadMonsterData();
      if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'Energia Insuficiente!');
      } else {
        toast.error(error.response?.data?.detail || 'Erro ao usar poder');
      }
    } finally {
      setUsingPower(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
      </div>
    );
  }

  if (!monsterData || !monsterData.monster) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600 font-nunito mb-4">Nenhum monstro encontrado</p>
          <Button onClick={() => navigate('/student/dashboard')}>Voltar ao Dashboard</Button>
        </div>
      </div>
    );
  }

  const { student, monster, powers } = monsterData;
  const cooperativeInfo = monsterData.cooperative_info;
  const currentEnergy = student.energy;
  const maxEnergy = student.max_energy || 50;
  const energyPercentage = (currentEnergy / maxEnergy) * 100;
  const unlockedPowers = powers.filter(p => student.level >= p.level_unlock);
  const lockedPowers = powers.filter(p => student.level < p.level_unlock);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Cost Change Alert Banner */}
      {showCostChangeAlert && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 px-4 flex items-center justify-center gap-3 animate-pulse shadow-lg">
          <Zap className="w-5 h-5 animate-bounce" />
          <span className="font-bold">O custo das habilidades foi ajustado pelo Mestre!</span>
          <Zap className="w-5 h-5 animate-bounce" />
        </div>
      )}

      <nav className={`bg-white border-b border-slate-200 ${showCostChangeAlert ? 'mt-12' : ''}`}>
        <div className="container mx-auto px-4 py-4">
          <Button data-testid="back-to-dashboard-btn" onClick={() => navigate('/student/dashboard')} variant="ghost" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Voltar
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {/* Monster Header */}
        <div className="bg-white rounded-3xl border-2 border-slate-100 p-8 mb-8 shadow-sm">
          <div className="flex items-center gap-6">
            <PokemonImage
              src={getMonsterGifByMonster(monster)}
              fallbackSrc={getMonsterFallbackGif(monster)}
              alt={getMonsterDisplayName(monster)}
              className="w-32 h-32 object-contain"
              fallbackSize={128}
            />
            <div className="flex-1">
              <h1 className="text-3xl font-outfit font-black text-slate-900 mb-2" data-testid="monster-name">
                {getMonsterDisplayName(monster)}
              </h1>
              <div className="flex items-center gap-3 mb-3">
                <span className={`px-3 py-1 rounded-md text-xs font-bold uppercase border ${getClassBadge(monster.class_type)}`}>
                  {getClassName(monster.class_type)}
                </span>
                <span className="bg-slate-900 text-white px-3 py-1 rounded-full text-xs font-bold">
                  Nivel {student.level}
                </span>
                <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-bold">
                  Estagio {monster.stage}
                </span>
              </div>
              {/* Energy Bar */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-blue-500" />
                    <span className="font-nunito font-bold text-slate-700">Energia</span>
                  </div>
                  <span data-testid="energy-display" className="font-nunito font-bold text-blue-600">
                    {currentEnergy}/{maxEnergy}
                  </span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-4 overflow-hidden">
                  <div
                    data-testid="energy-bar"
                    className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${energyPercentage}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Unlocked Powers */}
        <div className="mb-8">
          <h2 className="text-2xl font-outfit font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-secondary" />
            Poderes Desbloqueados ({unlockedPowers.length}/{powers.length})
          </h2>
          {unlockedPowers.length === 0 ? (
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-12 text-center">
              <Lock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-nunito">Nenhum poder desbloqueado ainda. Continue subindo de nivel!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {unlockedPowers.map((power) => (
                <PowerCard
                  key={power.power_id}
                  power={power}
                  unlocked={true}
                  currentEnergy={currentEnergy}
                  onUsePower={handleUsePower}
                  onUseCoopPower={handleCoopPowerClick}
                  isUsing={usingPower === power.power_id}
                  hasGroup={monsterData.has_group}
                  cooperativeInfo={cooperativeInfo}
                />
              ))}
            </div>
          )}
        </div>

        {/* Locked Powers */}
        {lockedPowers.length > 0 && (
          <div>
            <h2 className="text-2xl font-outfit font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Lock className="w-6 h-6 text-slate-400" />
              Poderes Bloqueados
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {lockedPowers.map((power) => (
                <PowerCard
                  key={power.power_id}
                  power={power}
                  unlocked={false}
                  currentLevel={student.level}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Cooperative Power Modal */}
      {showCoopModal && coopPower && (
        <CooperativeModal
          power={coopPower}
          members={groupMembers}
          groupName={groupName}
          loadingMembers={loadingMembers}
          selectedTargets={selectedTargets}
          onSelectTarget={handleSelectTarget}
          onSelectWholeGroup={handleSelectWholeGroup}
          onConfirm={handleUseCoopPower}
          onClose={() => { setShowCoopModal(false); setCoopPower(null); setSelectedTargets([]); }}
          currentEnergy={currentEnergy}
          totalCost={getCoopTotalCost()}
          canAfford={canAffordCoop()}
          isUsing={usingPower === coopPower?.power_id}
          cooperativeInfo={cooperativeInfo}
          coopSuccess={coopSuccess}
        />
      )}
    </div>
  );
};

// ============ PowerCard Component ============
const PowerCard = ({ power, unlocked, currentEnergy, currentLevel, onUsePower, onUseCoopPower, isUsing, hasGroup, cooperativeInfo }) => {
  const isCoop = power.is_cooperative;
  const canUse = unlocked && currentEnergy >= power.energy_cost && !isUsing;
  const [costUpdated, setCostUpdated] = useState(false);
  const prevCostRef = useRef(power.energy_cost);

  useEffect(() => {
    if (prevCostRef.current !== power.energy_cost) {
      setCostUpdated(true);
      setTimeout(() => setCostUpdated(false), 3000);
      prevCostRef.current = power.energy_cost;
    }
  }, [power.energy_cost]);

  const handleClick = () => {
    if (!canUse) return;
    if (isCoop && onUseCoopPower) {
      onUseCoopPower(power);
    } else if (onUsePower) {
      onUsePower(power);
    }
  };

  const effectIcon = cooperativeInfo?.effect_type === 'hp'
    ? <Heart className="w-4 h-4 text-rose-500" />
    : <Zap className="w-4 h-4 text-cyan-400" />;

  return (
    <div
      data-testid={isCoop ? 'power-card-cooperative' : (unlocked ? 'power-card-unlocked' : 'power-card-locked')}
      className={`rounded-2xl border-2 p-6 transition-all relative overflow-hidden ${
        isCoop && unlocked
          ? 'bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200 hover:border-indigo-400 hover:shadow-lg'
          : unlocked
            ? 'bg-white border-slate-100 hover:border-blue-200 hover:shadow-md'
            : 'bg-slate-50 border-slate-200'
      } ${costUpdated ? 'ring-2 ring-amber-400 ring-offset-2' : ''}`}
    >
      {/* Cooperative badge */}
      {isCoop && unlocked && (
        <div className="absolute top-3 right-3 flex items-center gap-1 bg-indigo-600 text-white px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
          <Users className="w-3 h-3" /> Cooperativo
        </div>
      )}

      <div className="flex items-start justify-between mb-4 pr-20">
        <div className="flex-1">
          <h3 className={`text-lg font-outfit font-bold mb-1 ${unlocked ? 'text-slate-900' : 'text-slate-400'}`}>
            {power.name}
          </h3>
          <p className={`text-sm font-nunito ${unlocked ? 'text-slate-600' : 'text-slate-400'}`}>
            {power.description}
          </p>
        </div>
        {!unlocked && <Lock className="w-5 h-5 text-slate-400 flex-shrink-0 ml-2" />}
      </div>

      {/* Effect info for cooperative powers */}
      {isCoop && unlocked && cooperativeInfo && (
        <div className="flex items-center gap-2 mb-3 bg-white/70 rounded-lg px-3 py-2 border border-indigo-100">
          {effectIcon}
          <span className="text-xs font-bold text-indigo-700 font-nunito">
            +{cooperativeInfo.effect_value} {cooperativeInfo.effect_type === 'hp' ? 'HP' : 'Energia'} por aliado
          </span>
          {!hasGroup && (
            <span className="text-[10px] text-orange-600 ml-auto font-bold">Sem grupo</span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-all ${
            costUpdated ? 'bg-amber-100 animate-pulse' : unlocked ? 'bg-blue-50' : 'bg-slate-100'
          }`}>
            <Zap className={`w-4 h-4 ${costUpdated ? 'text-amber-500 animate-bounce' : unlocked ? 'text-blue-500' : 'text-slate-300'}`} />
            <span data-testid="power-energy-cost" className={`text-sm font-bold font-nunito ${costUpdated ? 'text-amber-600' : unlocked ? 'text-slate-900' : 'text-slate-400'}`}>
              {power.energy_cost}
            </span>
          </div>
          <div className={`text-xs font-bold ${unlocked ? 'text-green-600' : 'text-orange-600'}`}>
            {unlocked ? 'Desbloqueado' : `Nivel ${power.level_unlock}`}
          </div>
        </div>
      </div>

      {!unlocked && currentLevel && (
        <div className="mt-3 pt-3 border-t border-slate-200">
          <p className="text-xs text-slate-500 font-nunito">
            Faltam {power.level_unlock - currentLevel} niveis para desbloquear
          </p>
        </div>
      )}

      {unlocked && (
        <div className="mt-4">
          <Button
            data-testid={isCoop ? 'use-coop-power-btn' : 'use-power-btn'}
            onClick={handleClick}
            disabled={!canUse || isUsing}
            className={`w-full h-10 rounded-xl font-bold transition-all ${
              isCoop && canUse
                ? 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white'
                : canUse
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            {isUsing ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Usando...
              </span>
            ) : isCoop ? (
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                {currentEnergy >= power.energy_cost ? 'Ajudar Aliado' : 'Energia Insuficiente'}
              </span>
            ) : currentEnergy >= power.energy_cost ? (
              'Usar Poder'
            ) : (
              'Energia Insuficiente'
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

// ============ Cooperative Modal ============
const CooperativeModal = ({
  power, members, groupName, loadingMembers, selectedTargets,
  onSelectTarget, onSelectWholeGroup, onConfirm, onClose,
  currentEnergy, totalCost, canAfford, isUsing, cooperativeInfo, coopSuccess
}) => {
  const isAllSelected = members.length > 0 && selectedTargets.length === members.length;
  const effectLabel = cooperativeInfo?.effect_type === 'hp' ? 'HP' : 'Energia';
  const effectIcon = cooperativeInfo?.effect_type === 'hp'
    ? <Heart className="w-5 h-5 text-rose-500" />
    : <Zap className="w-5 h-5 text-cyan-400" />;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="coop-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Success overlay */}
        {coopSuccess && <CoopSuccessOverlay data={coopSuccess} />}

        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-700 p-5 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-2 left-4 w-16 h-16 bg-white/20 rounded-full blur-xl animate-pulse" />
            <div className="absolute bottom-1 right-8 w-12 h-12 bg-white/20 rounded-full blur-lg animate-pulse" style={{ animationDelay: '0.5s' }} />
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition"
            data-testid="close-coop-modal"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-outfit font-bold">{power.name}</h3>
              <p className="text-sm text-white/80 font-nunito">Selecione aliado(s) do grupo</p>
            </div>
          </div>
          {groupName && (
            <div className="mt-3 bg-white/15 rounded-lg px-3 py-1.5 inline-flex items-center gap-2">
              <Shield className="w-3.5 h-3.5" />
              <span className="text-xs font-bold">{groupName}</span>
            </div>
          )}
        </div>

        {/* Effect info */}
        <div className="mx-5 mt-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-3 flex items-center gap-3 border border-indigo-100">
          {effectIcon}
          <div>
            <p className="text-sm font-bold text-indigo-800 font-nunito">
              +{cooperativeInfo?.effect_value} {effectLabel} por aliado
            </p>
            <p className="text-xs text-indigo-600/70">
              Custo: {power.energy_cost} energia por alvo
            </p>
          </div>
        </div>

        {/* Members list */}
        <div className="p-5">
          {loadingMembers ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-nunito text-sm">Nenhum aliado no grupo</p>
            </div>
          ) : (
            <>
              {/* Select All */}
              <button
                data-testid="select-whole-group-btn"
                onClick={onSelectWholeGroup}
                className={`w-full flex items-center gap-3 p-3 rounded-xl mb-3 border-2 transition-all ${
                  isAllSelected
                    ? 'bg-indigo-50 border-indigo-400 shadow-sm'
                    : 'bg-slate-50 border-slate-200 hover:border-indigo-200'
                }`}
              >
                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                  isAllSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'
                }`}>
                  {isAllSelected && <span className="text-white text-xs font-bold">&#10003;</span>}
                </div>
                <Users className="w-4 h-4 text-indigo-500" />
                <span className="font-bold text-sm text-slate-800 font-nunito">Todo o Grupo</span>
                <span className="ml-auto text-xs font-bold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full">
                  {power.energy_cost * members.length} energia
                </span>
              </button>

              <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1">
                {members.map((member) => {
                  const isSelected = selectedTargets.includes(member.student_id);
                  const isHpFull = cooperativeInfo?.effect_type === 'hp' && member.hp >= member.max_hp;
                  const isEnergyFull = cooperativeInfo?.effect_type === 'energy' && member.energy >= member.max_energy;
                  const isFull = isHpFull || isEnergyFull;
                  return (
                    <button
                      key={member.student_id}
                      data-testid={`select-target-${member.student_id}`}
                      onClick={() => onSelectTarget(member.student_id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'bg-indigo-50 border-indigo-400 shadow-sm'
                          : 'bg-white border-slate-200 hover:border-indigo-200'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                        isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'
                      }`}>
                        {isSelected && <span className="text-white text-xs font-bold">&#10003;</span>}
                      </div>

                      {/* Member avatar area */}
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden">
                        {member.monster_index !== null && member.class_type ? (
                          <PokemonImage
                            src={getMonsterGifByMonster({...member, stage: member.monster_stage || 1})}
                            fallbackSrc={getMonsterFallbackGif({...member, stage: member.monster_stage || 1})}
                            alt={member.monster_name || ''}
                            className="w-8 h-8 object-contain"
                            fallbackSize={32}
                          />
                        ) : (
                          <span className="text-lg">?</span>
                        )}
                      </div>

                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-bold text-slate-800 truncate font-nunito">{member.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {cooperativeInfo?.effect_type === 'hp' ? (
                            <div className="flex items-center gap-1">
                              <Heart className="w-3 h-3 text-rose-400" />
                              <span className={`text-[11px] font-bold ${isFull ? 'text-green-600' : 'text-rose-600'}`}>
                                {member.hp}/{member.max_hp}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <Zap className="w-3 h-3 text-cyan-400" />
                              <span className={`text-[11px] font-bold ${isFull ? 'text-green-600' : 'text-cyan-600'}`}>
                                {member.energy}/{member.max_energy}
                              </span>
                            </div>
                          )}
                          {isFull && (
                            <span className="text-[10px] text-green-600 font-bold bg-green-50 px-1.5 rounded">MAX</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {members.length > 0 && !coopSuccess && (
          <div className="px-5 pb-5">
            <div className="flex items-center justify-between mb-3 text-sm">
              <span className="text-slate-500 font-nunito">
                {selectedTargets.length} alvo(s) selecionado(s)
              </span>
              <span className={`font-bold font-nunito ${canAfford ? 'text-indigo-700' : 'text-red-600'}`}>
                Custo: {totalCost} / {currentEnergy} energia
              </span>
            </div>
            <Button
              data-testid="confirm-coop-power-btn"
              onClick={onConfirm}
              disabled={selectedTargets.length === 0 || !canAfford || isUsing}
              className={`w-full h-12 rounded-xl font-bold text-base transition-all ${
                selectedTargets.length > 0 && canAfford
                  ? 'bg-gradient-to-r from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800 text-white shadow-lg shadow-indigo-300/40'
                  : 'bg-slate-200 text-slate-400'
              }`}
            >
              {isUsing ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Canalizando...
                </span>
              ) : !canAfford && selectedTargets.length > 0 ? (
                'Energia Insuficiente'
              ) : (
                <span className="flex items-center gap-2">
                  {effectIcon}
                  Usar {power.name}
                </span>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ Success Overlay (Light Beam Animation) ============
const CoopSuccessOverlay = ({ data }) => {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none" data-testid="coop-success-overlay">
      {/* Light beam background */}
      <div className="absolute inset-0 bg-gradient-to-b from-yellow-300/30 via-white/60 to-indigo-400/20 animate-pulse" />

      {/* Rays */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute top-1/2 left-1/2 w-1 bg-gradient-to-b from-yellow-300 to-transparent opacity-60"
            style={{
              height: '200%',
              transform: `translate(-50%, -50%) rotate(${i * 45}deg)`,
              animation: `coop-ray-pulse 1.5s ease-in-out infinite`,
              animationDelay: `${i * 0.1}s`
            }}
          />
        ))}
      </div>

      {/* Central icon */}
      <div className="relative z-20 flex flex-col items-center gap-3">
        <div className="w-20 h-20 rounded-full bg-white shadow-2xl shadow-yellow-400/50 flex items-center justify-center animate-bounce">
          {data.effectType === 'hp' ? (
            <Heart className="w-10 h-10 text-rose-500 drop-shadow" />
          ) : (
            <Zap className="w-10 h-10 text-cyan-500 drop-shadow" />
          )}
        </div>
        <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 shadow-lg text-center">
          <p className="text-sm font-bold text-indigo-800 font-outfit">{data.powerName}</p>
          <p className="text-xs text-indigo-600 font-nunito">
            +{data.effectValue} {data.effectType === 'hp' ? 'HP' : 'Energia'} para {data.targets?.length || 0} aliado(s)!
          </p>
        </div>
      </div>

      {/* Floating particles */}
      {[...Array(12)].map((_, i) => (
        <div
          key={`particle-${i}`}
          className={`absolute w-2 h-2 rounded-full ${data.effectType === 'hp' ? 'bg-rose-400' : 'bg-cyan-400'}`}
          style={{
            left: `${20 + Math.random() * 60}%`,
            top: `${20 + Math.random() * 60}%`,
            opacity: 0.7,
            animation: `coop-particle-float 2s ease-out infinite`,
            animationDelay: `${i * 0.15}s`
          }}
        />
      ))}

      <style>{`
        @keyframes coop-ray-pulse {
          0%, 100% { opacity: 0.2; transform: translate(-50%, -50%) rotate(var(--rotation)) scaleY(0.8); }
          50% { opacity: 0.6; transform: translate(-50%, -50%) rotate(var(--rotation)) scaleY(1.1); }
        }
        @keyframes coop-particle-float {
          0% { transform: translateY(0) scale(1); opacity: 0.7; }
          100% { transform: translateY(-40px) scale(0); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default StudentPowers;
